import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ensureContentFeedProfile } from '@sola/my-sdk/content-feed/profile.ts';
import { fetchContentFeedEntries } from '@sola/my-sdk/content-feed/requests.ts';
import type { ContentFeedSearchRequest } from '@sola/schema/dotcom/proxy-api/content-feed.ts';
import { useSponsorships } from '@sola/supporter-sdk/hooks/commitments.ts';
import { usePaymentMethodsWithIssues } from '@sola/supporter-sdk/hooks/payment-methods.ts';
import { trackError } from '@sola/tracking/components/datalayer.tsx';
import { evaluateThreshold, filterByMatch } from './content-feed.suppression.ts';
import type { ContentFeedEntryResolved, ContentFeedListResponse } from './content-feed.types.ts';
type FetchFn = (params: {
  cursor: string | null;
}) => Promise<ContentFeedListResponse>;

/**
 * Sources explicitly allowed in the feed (whitelist). Only these sources will
 * appear — new sources added to the API require a code change to opt in.
 */
const allowedSources: ContentFeedSearchRequest['sources'] = ['community', 'causes', 'compassion.media'];

/** Maximum age of entries to fetch (in days). Pushed server-side via publishedAfter. */
const maxAgeDays = 365;

/**
 * Hook that manages the paginated content feed state with suppression logic.
 * Handles fetching entries, pagination cursor, loading state, and content suppression.
 */
export const useContentFeed = (limit = 10) => {
  const {
    t,
    i18n
  } = useTranslation(['my']);
  const [rawEntries, setRawEntries] = useState<ContentFeedEntryResolved[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [isFeedLoading, setIsFeedLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);

  // Sponsorship commitments: resolve FCP IDs, participant IDs, and country codes
  const {
    sponsorships,
    isLoading: isSponsorshipsLoading,
    isError: isSponsorshipsError
  } = useSponsorships();
  const isCommitmentsReady = !isSponsorshipsLoading || isSponsorshipsError;

  // Derive unique FCP IDs from commitments.
  // FCP ID = first 6 chars of participant localId (e.g. "DR054002196" → "DR0540").
  const fcpIds = useMemo(() => [...new Set(sponsorships.map(c => c.participant?.localId?.slice(0, 6)).filter((id): id is string => !!id))], [sponsorships]);

  // Build a localId → preferredName map for banner text resolution
  const participantNameMap = useMemo(() => new Map(sponsorships.flatMap(c_0 => {
    const localId = c_0.participant?.localId;
    if (!localId) {
      return [];
    }
    const name = c_0.participant?.name || localId;
    return [[localId, name]] as const;
  })), [sponsorships]);

  // Financial standing: use the existing payment methods hook
  const {
    paymentMethodsWithIssues,
    isLoading: isPaymentDataLoading
  } = usePaymentMethodsWithIssues();
  const inGoodStanding = !isPaymentDataLoading && paymentMethodsWithIssues.length === 0;

  // Combined loading state: show skeleton until all dependencies resolve
  const isLoading = isFeedLoading || isPaymentDataLoading || !isCommitmentsReady;
  const locale = i18n.language;

  // Compute a 365-day recency cutoff for server-side filtering.
  const publishedAfter = useMemo(() => new Date(Date.now() - maxAgeDays * 86_400_000).toISOString(), []);
  const fetchEntries: FetchFn = params => fetchContentFeedEntries({
    ...params,
    locale,
    fcpIds,
    sources: allowedSources,
    publishedAfter,
    includeFinancialAppeals: inGoodStanding,
    includeNearbyEntries: true,
    sortBy: 'proximity',
    sortDirection: 'desc',
    limit
  });

  // Fetch entries once all dependencies are resolved (commitments + payment standing).
  // Gating on isPaymentDataLoading prevents a redundant first request with
  // includeFinancialAppeals: false that would immediately be superseded when
  // payment data resolves and inGoodStanding flips to true.
  useEffect(() => {
    if (!isCommitmentsReady || isPaymentDataLoading) {
      return;
    }

    // Skip if no sponsored children resolved
    if (fcpIds.length === 0) {
      setRawEntries([]);
      setHasMore(false);
      setIsFeedLoading(false);
      return;
    }
    let cancelled = false;
    setIsFeedLoading(true);

    // Ensure the supporter has a profile on the Content API (fire-and-forget).
    // This guarantees they can react/comment without a 403.
    ensureContentFeedProfile();
    fetchEntries({
      cursor: null
    }).then(response => {
      if (!cancelled) {
        setRawEntries(response.entries);
        setCursor(response.cursor);
        setHasMore(response.cursor !== null);
      }
    }).catch(() => {
      if (!cancelled) {
        setRawEntries([]);
        setHasMore(false);
        trackError('content_feed_load_failed');
      }
    }).finally(() => {
      if (!cancelled) {
        setIsFeedLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
    // fcpIds is memoized and only changes when sponsorships change.
    // locale triggers re-fetch when language changes.
    // inGoodStanding triggers re-fetch when payment standing changes.
    // isPaymentDataLoading gates initial fetch until payment standing is known.
  }, [isCommitmentsReady, isPaymentDataLoading, fcpIds, locale, inGoodStanding]);
  const loadMore = useCallback(async () => {
    if (isFeedLoading || !hasMore) {
      return;
    }
    setIsFeedLoading(true);
    try {
      const response_0 = await fetchEntries({
        cursor
      });
      setRawEntries(prev => [...prev, ...response_0.entries]);
      setCursor(response_0.cursor);
      setHasMore(response_0.cursor !== null);
    } catch {
      trackError('content_feed_load_more_failed');
    } finally {
      setIsFeedLoading(false);
    }
  }, [cursor, fetchEntries, hasMore, isFeedLoading]);

  // Apply lightweight client-side guards:
  // - filterByMatch: defensive check that entries have match context
  // - evaluateThreshold: don't render a nearly-empty feed
  const {
    entries,
    shouldRender
  } = useMemo(() => {
    const afterMatch = filterByMatch(rawEntries);
    return {
      entries: afterMatch,
      shouldRender: evaluateThreshold(afterMatch)
    };
  }, [rawEntries]);
  return {
    entries,
    isLoading,
    hasMore,
    loadMore,
    t,
    shouldRender,
    participantNameMap
  };
};